import { z } from 'zod';
// Email templates: the block document plus the request shapes for the admin routes.
//
// Deliberately duplicated from backend/src/schemas/email_templates.ts and
// backend/src/domain/entities/email_template_entity.ts. The backend cannot import this
// package — perf.yml and zap.yml build the backend without building shared — so the two
// copies are pinned together by backend/tests/contracts.test.ts instead of by an import.
// Change one, change the other, and that test tells you if you forgot.
export const EMAIL_BLOCK_TYPES = [
    'heading',
    'paragraph',
    'button',
    'note',
    'divider',
    'spacer',
    'image',
    'image_text',
    'step',
];
export const SPACER_SIZES = ['sm', 'md', 'lg'];
export const EMAIL_MERGE_VAR_NAMES = [
    'email',
    'first_name',
    'last_name',
    'event_name',
    'session_name',
    'company',
    'designation',
    'form_link',
];
export const EMAIL_HEADING_MAX = 200;
export const EMAIL_PARAGRAPH_MAX = 2000;
export const EMAIL_NOTE_MAX = 500;
export const EMAIL_LABEL_MAX = 80;
export const EMAIL_URL_MAX = 2000;
export const EMAIL_ALT_MAX = 200;
export const EMAIL_STEP_NUM_MAX = 4;
export const EMAIL_STEP_HEADING_MAX = 120;
export const EMAIL_STEP_BODY_MAX = 1000;
export const EMAIL_SUBJECT_MAX = 200;
export const EMAIL_PREHEADER_MAX = 200;
export const EMAIL_FOOTER_NOTE_MAX = 500;
export const EMAIL_MAX_BLOCKS = 100;
export const EmailBlockSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('heading'), text: z.string().min(1).max(EMAIL_HEADING_MAX) }),
    z.object({ type: z.literal('paragraph'), text: z.string().min(1).max(EMAIL_PARAGRAPH_MAX) }),
    z.object({
        type: z.literal('button'),
        label: z.string().min(1).max(EMAIL_LABEL_MAX),
        href: z.string().min(1).max(EMAIL_URL_MAX),
    }),
    z.object({ type: z.literal('note'), text: z.string().min(1).max(EMAIL_NOTE_MAX) }),
    z.object({ type: z.literal('divider') }),
    z.object({ type: z.literal('spacer'), size: z.enum(SPACER_SIZES) }),
    z.object({
        type: z.literal('image'),
        src: z.string().min(1).max(EMAIL_URL_MAX),
        alt: z.string().max(EMAIL_ALT_MAX),
        width: z.number().int().min(16).max(480),
        // Optional, defaulted to 'left' at render: already-saved docs stay valid
        // and render exactly as they did.
        align: z.enum(['left', 'center', 'right']).optional(),
    }),
    z.object({
        type: z.literal('image_text'),
        src: z.string().min(1).max(EMAIL_URL_MAX),
        alt: z.string().max(EMAIL_ALT_MAX),
        width: z.number().int().min(16).max(300),
        side: z.enum(['left', 'right']),
        text: z.string().min(1).max(EMAIL_PARAGRAPH_MAX),
    }),
    z.object({
        type: z.literal('step'),
        num: z.string().min(1).max(EMAIL_STEP_NUM_MAX),
        heading: z.string().min(1).max(EMAIL_STEP_HEADING_MAX),
        body: z.string().min(1).max(EMAIL_STEP_BODY_MAX),
        note: z.string().max(EMAIL_NOTE_MAX).optional(),
    }),
]);
export const EmailTemplateDocSchema = z.object({
    subject: z.string().min(1).max(EMAIL_SUBJECT_MAX),
    preheader: z.string().max(EMAIL_PREHEADER_MAX).default(''),
    blocks: z.array(EmailBlockSchema).max(EMAIL_MAX_BLOCKS),
    footerNote: z.string().max(EMAIL_FOOTER_NOTE_MAX).default(''),
});
// --- served editor configuration (GET .../email-blocks/types) ---
export const BlockFieldSpecSchema = z.object({
    key: z.string(),
    label: z.string(),
    control: z.enum(['text', 'textarea', 'url', 'image_url', 'select', 'number']),
    required: z.boolean(),
    maxLength: z.number().int().optional(),
    min: z.number().int().optional(),
    max: z.number().int().optional(),
    default: z.number().int().optional(),
    options: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
    allowsVariables: z.boolean(),
    hint: z.string().optional(),
});
export const BlockTypeSpecSchema = z.object({
    type: z.string(),
    label: z.string(),
    description: z.string(),
    fields: z.array(BlockFieldSpecSchema),
});
export const MergeVarSpecSchema = z.object({
    name: z.enum(EMAIL_MERGE_VAR_NAMES),
    label: z.string(),
    sample: z.string(),
});
// --- request shapes ---
export const CreateEmailTemplateRequestSchema = z.object({
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).default(''),
});
/** Rename / reword. Reaches the container only; a saved version is never editable. */
export const UpdateEmailTemplateRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(120).optional(),
    description: z.string().trim().max(500).optional(),
})
    .strict()
    .refine((v) => v.name !== undefined || v.description !== undefined, {
    message: 'Provide name or description',
    path: ['name'],
});
export const SaveEmailTemplateVersionRequestSchema = EmailTemplateDocSchema;
// Strict: a name outside the whitelist is a 400 that names it, not a silent drop.
export const MergeVarsPatchSchema = z
    .object(Object.fromEntries(EMAIL_MERGE_VAR_NAMES.map((n) => [n, z.string().max(500).optional()])))
    .strict();
/** Preview and test-send share one body. Test-send has no address field, by design. */
export const PreviewEmailTemplateRequestSchema = z
    .object({
    doc: EmailTemplateDocSchema.optional(),
    versionId: z.string().uuid().optional(),
    vars: MergeVarsPatchSchema.optional(),
})
    .strict();
export const TestSendEmailTemplateRequestSchema = PreviewEmailTemplateRequestSchema;
//# sourceMappingURL=email_templates.js.map